import React, { useState } from 'react';
import { Edit, Trash2 } from 'lucide-react';
import { useRiskContext } from '../context/RiskContext.jsx';

export default function RiskRegisterTable({ columns, setColumns }) {
  const {
    getSortedRisks,
    getRiskPriority,
    handleSort,
    getSortIcon,
    editingCell,
    editingValue,
    setEditingValue,
    saveInlineEdit,
    handleInlineKeyPress,
    startEditing,
    openRiskDetail,
    removeRisk,
    categories,
    riskActions,
    statusOptions,
    matrixConfig
  } = useRiskContext();

  const internalDefault = [
    { key: 'id', label: 'ID', editable: false },
    { key: 'description', label: 'Description', editable: false },
    { key: 'initialLikelihood', label: 'Initial L', editable: true, type: 'select' },
    { key: 'initialImpact', label: 'Initial I', editable: true, type: 'select' },
    { key: 'initialScore', label: 'Initial Score', editable: false, badge: 'initial' },
    { key: 'category', label: 'Category', editable: true, type: 'select' },
    { key: 'action', label: 'Action', editable: true, type: 'select' },
    { key: 'actionDescription', label: 'Action Description', editable: true, type: 'textarea' },
    { key: 'actionOwner', label: 'Action Owner', editable: true },
    { key: 'deadline', label: 'Deadline', editable: true, type: 'date' },
    { key: 'status', label: 'Status', editable: true, type: 'select' },
    { key: 'likelihood', label: 'Residual Likelihood', editable: true, type: 'select' },
    { key: 'impact', label: 'Residual Impact', editable: true, type: 'select' },
    { key: 'riskScore', label: 'Residual Score', editable: false, badge: 'residual' }
  ];

  if (!columns) {
    columns = internalDefault;
  }

  const handleColumnDragStart = (e, fromIdx) => {
    e.dataTransfer.setData('colFrom', fromIdx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDrop = (e, toIdx) => {
    const fromIdx = parseInt(e.dataTransfer.getData('colFrom'), 10);
    if (isNaN(fromIdx)) return;
    if (fromIdx === toIdx) return;
    setColumns(prev => {
      const copy = [...prev];
      copy.splice(toIdx, 0, copy.splice(fromIdx, 1)[0]);
      return copy;
    });
  };

  const renderCellContent = (risk, col) => {
    if (col.badge === 'initial') {
      const initialPriority = getRiskPriority(risk.initialLikelihood || risk.likelihood, risk.initialImpact || risk.impact);
      return (
        <span className="px-2 py-1 rounded text-xs font-medium text-white" style={{ backgroundColor: initialPriority.color }}>
          {initialPriority.level}
        </span>
      );
    }
    if (col.badge === 'residual') {
      const priority = getRiskPriority(risk.likelihood, risk.impact);
      return (
        <span className="px-2 py-1 rounded text-xs font-medium text-white" style={{ backgroundColor: priority.color }}>
          {priority.level}
        </span>
      );
    }

    if (!col.editable) {
      // Non-editable plain text
      return risk[col.key] || <span className="text-gray-400 italic">—</span>;
    }

    // Editable -> defer to existing helper
    const type = col.type || 'text';
    return renderEditableCell(risk, col.key, risk[col.key], type);
  };

  const renderEditableCell = (risk, field, value, type = 'text') => {
    const isEditing = editingCell?.riskId === risk.id && editingCell?.field === field;

    if (isEditing) {
      if (type === 'select') {
        let options = [];
        if (field === 'category') {
          options = categories.map(c => ({ value: c, label: c }));
        } else if (field === 'action') {
          options = riskActions.map(a => ({ value: a, label: a }));
        } else if (field === 'status') {
          options = statusOptions.map(s => ({ value: s, label: s }));
        } else if (field === 'likelihood' || field === 'initialLikelihood') {
          const levels = matrixConfig.likelihoodLevels || matrixConfig.probabilityLevels || [];
          options = levels.map((lbl, idx) => ({ value: idx + 1, label: lbl }));
        } else if (field === 'impact' || field === 'initialImpact') {
          const levels = matrixConfig.impactLevels || [];
          options = levels.map((lbl, idx) => ({ value: idx + 1, label: lbl }));
        }

        return (
          <select
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={saveInlineEdit}
            onKeyDown={handleInlineKeyPress}
            className="w-full px-2 py-1 border rounded text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      } else if (type === 'textarea') {
        return (
          <textarea
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={saveInlineEdit}
            onKeyDown={handleInlineKeyPress}
            className="w-full px-2 py-1 border rounded text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={2}
            autoFocus
          />
        );
      } else {
        return (
          <input
            type={type}
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onBlur={saveInlineEdit}
            onKeyDown={handleInlineKeyPress}
            className="w-full px-2 py-1 border rounded text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        );
      }
    }

    // Enhanced tooltips for different field types
    const getFieldTooltip = (field, value, risk) => {
      const priority = getRiskPriority(risk.likelihood, risk.impact);
      
      switch (field) {
        case 'likelihood':
        case 'initialLikelihood':
          const isInitial = field.includes('initial');
          const likelihoodLevels = matrixConfig.likelihoodLevels || matrixConfig.probabilityLevels || [];
          const currentLevel = likelihoodLevels[value - 1] || 'Unknown';
          return [
            `${isInitial ? 'Initial' : 'Residual'} Likelihood: ${currentLevel} (${value}/5)`,
            '',
            'This represents the probability that the risk will occur',
            `Current priority: ${priority.level}`,
            '',
            'Click to edit • Use 1-5 scale',
            '1 = Very Low, 5 = Very High'
          ].join('\n');
          
        case 'impact':
        case 'initialImpact':
          const isInitialImpact = field.includes('initial');
          const impactLevels = matrixConfig.impactLevels || [];
          const impactLevel = impactLevels[value - 1] || 'Unknown';
          return [
            `${isInitialImpact ? 'Initial' : 'Residual'} Impact: ${impactLevel} (${value}/5)`,
            '',
            'This represents the severity if the risk occurs',
            `Current priority: ${priority.level}`,
            '',
            'Click to edit • Use 1-5 scale',
            '1 = Very Low, 5 = Very High'
          ].join('\n');
          
        case 'category':
          return [
            `Risk Category: ${value || 'Not assigned'}`,
            '',
            'Categorizes risks for better organization',
            'Common categories:',
            '• Technical - System/technology risks',
            '• Financial - Budget/cost risks',
            '• Resource - People/skills risks',
            '• Scope - Requirements/scope risks',
            '• External - Market/regulatory risks',
            '• Quality - Deliverable quality risks',
            '',
            'Click to edit or assign category'
          ].join('\n');
          
        case 'action':
          return [
            `Risk Response: ${value || 'Not defined'}`,
            '',
            'Defines how to handle this risk:',
            '• Monitor - Watch and track the risk',
            '• Mitigate - Reduce likelihood/impact',
            '• Transfer - Shift risk to third party',
            '• Accept - Acknowledge and live with risk',
            '• Avoid - Eliminate the risk entirely',
            '• Escalate - Raise to higher authority',
            '',
            'Click to edit response strategy'
          ].join('\n');
          
        case 'status':
          const statusEmoji = {
            'Not Started': '⭕',
            'In Progress': '🔄',
            'Completed': '✅',
            'On Hold': '⏸️',
            'Overdue': '⚠️'
          };
          return [
            `Status: ${statusEmoji[value] || '📋'} ${value || 'Not set'}`,
            '',
            'Tracks progress of risk management actions',
            '',
            'Available statuses:',
            '⭕ Not Started - No action taken yet',
            '🔄 In Progress - Currently being addressed',
            '✅ Completed - Risk management complete',
            '⏸️ On Hold - Temporarily paused',
            '⚠️ Overdue - Past deadline',
            '',
            'Click to update status'
          ].join('\n');
          
        case 'deadline':
          const deadlineDate = value ? new Date(value) : null;
          const isOverdue = deadlineDate && deadlineDate < new Date();
          const daysUntil = deadlineDate ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
          
          return [
            `Deadline: ${deadlineDate ? deadlineDate.toLocaleDateString() : 'Not set'}`,
            daysUntil !== null ? (
              isOverdue ? `⚠️ ${Math.abs(daysUntil)} days overdue` : 
              daysUntil === 0 ? '🔥 Due today!' :
              daysUntil <= 7 ? `⏰ ${daysUntil} days remaining` :
              `📅 ${daysUntil} days remaining`
            ) : '',
            '',
            'Target date for completing risk actions',
            isOverdue ? '⚠️ This risk is overdue - immediate attention needed' : '',
            '',
            'Click to set or update deadline'
          ].filter(Boolean).join('\n');
          
        case 'actionOwner':
          return [
            `Action Owner: ${value || 'Unassigned'}`,
            '',
            'Person responsible for managing this risk',
            value ? `Contact ${value} for updates` : '⚠️ No owner assigned - risk may be neglected',
            '',
            'Best practices:',
            '• Assign a single point of accountability',
            '• Ensure owner has authority to act',
            '• Regular check-ins with owner',
            '',
            'Click to assign or change owner'
          ].join('\n');
          
        case 'actionDescription':
          return [
            'Action Description',
            '',
            'Detailed plan for managing this risk',
            value ? `Current plan: ${value.substring(0, 100)}${value.length > 100 ? '...' : ''}` : '⚠️ No action plan defined',
            '',
            'Should include:',
            '• Specific steps to take',
            '• Resources required',
            '• Success criteria',
            '• Monitoring approach',
            '',
            'Click to add or edit action plan'
          ].join('\n');
          
        default:
          return `Click to edit ${field} • Current value: ${value || 'Not set'}`;
      }
    };

    return (
      <div
        className="cursor-pointer hover:bg-gray-50 p-1 rounded min-h-[24px]"
        onClick={() => startEditing(risk.id, field, value)}
        title={getFieldTooltip(field, value, risk)}
      >
        {(() => {
          let display = value;
          if (field === 'likelihood' || field === 'initialLikelihood') {
            // Handle both likelihoodLevels and probabilityLevels for compatibility
            const levels = matrixConfig.likelihoodLevels || matrixConfig.probabilityLevels || [];
            display = levels[value - 1] || value;
          } else if (field === 'impact' || field === 'initialImpact') {
            const levels = matrixConfig.impactLevels || [];
            display = levels[value - 1] || value;
          }
          return display || <span className="text-gray-400 italic">Click to edit</span>;
        })()}
      </div>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border text-xs sm:text-sm">
        <thead>
          <tr className="bg-gray-100">
            {columns.map((col, idx) => (
              <th
                key={col.key}
                className="border px-4 py-2 text-left cursor-pointer hover:bg-gray-200 select-none"
                onClick={() => handleSort(col.key)}
                draggable
                onDragStart={(e)=>handleColumnDragStart(e, idx)}
                onDragOver={(e)=>e.preventDefault()}
                onDrop={(e)=>handleColumnDrop(e, idx)}
              >
                <div className="flex items-center justify-between">
                  {col.label} {getSortIcon(col.key)}
                </div>
              </th>
            ))}
            <th className="border px-4 py-2 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          {getSortedRisks.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="border px-4 py-2 text-center text-gray-400">
                No risks to display
              </td>
            </tr>
          ) : (
            getSortedRisks.map((risk) => {
              return (
                <tr key={risk.id} className="hover:bg-gray-50">
                  {columns.map(col => (
                    <td key={col.key} className="border px-2 py-2 max-w-xs">
                      {col.key === 'description' ? (
                        <div 
                          className="cursor-pointer hover:underline text-blue-700" 
                          onClick={() => openRiskDetail(risk)} 
                          title={[
                            `Risk #${risk.id}: ${risk.description}`,
                            '',
                            `Priority: ${getRiskPriority(risk.likelihood, risk.impact).level}`,
                            `Owner: ${risk.actionOwner || 'Unassigned'}`,
                            `Status: ${risk.status}`,
                            risk.deadline ? `Deadline: ${new Date(risk.deadline).toLocaleDateString()}` : 'No deadline',
                            '',
                            'Click to open detailed risk editor',
                            '• Update all risk information',
                            '• Use interactive risk matrix',
                            '• Set initial and residual scores'
                          ].join('\n')}
                        >
                          {risk.description || <span className="text-gray-400 italic">No description</span>}
                        </div>
                      ) : (
                        renderCellContent(risk, col)
                      )}
                    </td>
                  ))}
                  <td className="border px-2 py-2">
                    <div className="flex gap-1">
                      <button
                        onClick={() => openRiskDetail(risk)}
                        className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                        title={[
                          `Edit Risk #${risk.id} Details`,
                          '',
                          'Opens comprehensive risk editor:',
                          '• Interactive risk matrix',
                          '• Initial vs residual scoring',
                          '• Complete action planning',
                          '• Status and deadline tracking',
                          '',
                          'Full editing capabilities for all risk data'
                        ].join('\n')}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => {
                          const priority = getRiskPriority(risk.likelihood, risk.impact);
                          if (window.confirm([
                            `Delete Risk #${risk.id}?`,
                            '',
                            `"${risk.description}"`,
                            '',
                            `Priority: ${priority.level}`,
                            `Owner: ${risk.actionOwner || 'Unassigned'}`,
                            `Status: ${risk.status}`,
                            '',
                            '⚠️ This action cannot be undone.',
                            'All risk data will be permanently lost.'
                          ].join('\n'))) {
                            removeRisk(risk.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                        title={[
                          `Delete Risk #${risk.id}`,
                          '',
                          '⚠️ Permanent deletion warning:',
                          '• Risk description and scores',
                          '• Action plans and deadlines', 
                          '• Progress tracking data',
                          '• Historical information',
                          '',
                          'This action cannot be undone!'
                        ].join('\n')}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
} 